/*
 * Native predictive search — replaces the paid CloudSearch instant-search
 * dropdown with Shopify's free, built-in Predictive Search API. Results come
 * from /search/suggest rendered through sections/predictive-search-data
 * (so Liquid can price Amazon-only books), falling back to
 * /search/suggest.json. Product-only, editorial-styled, keyboard
 * accessible. Attaches to any input marked [data-predictive-search-input].
 *
 * No dependencies. Self-initialises on DOMContentLoaded.
 */
(function () {
  'use strict';

  var ENDPOINT   = '/search/suggest.json';
  var SECTION_ENDPOINT = '/search/suggest';
  var SECTION_ID = 'predictive-search-data';
  var LIMIT      = 8;
  var MIN_CHARS  = 2;
  var DEBOUNCE   = 220;

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Shopify CDN honours a width query param for on-the-fly thumbnails.
  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // price arrives as a major-unit decimal string ("30.00"); render it
  // through the shop's money_format so currency + separators are correct.
  function formatMoney(price, moneyFormat) {
    var amount = parseFloat(price);
    if (isNaN(amount)) return '';
    var fmt = moneyFormat || '${{amount}}';
    var withDec = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var noDec   = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, withDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDec)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec)
      .replace(/\{\{[^}]*\}\}/g, withDec);
  }

  function allResultsHref(query) {
    return '/search?q=' + encodeURIComponent(query) + '&type=product';
  }

  function footerHtml(query) {
    return '<a class="predictive-search__all" data-ps-all href="' +
      escapeHtml(allResultsHref(query)) + '">Show all results for “' +
      escapeHtml(query) + '”</a>';
  }

  function render(panel, products, query, moneyFormat) {
    if (!products.length) {
      panel.innerHTML =
        '<p class="predictive-search__empty">No matches for “' +
        escapeHtml(query) + '”.</p>' + footerHtml(query);
      return;
    }
    var items = products.map(function (p, i) {
      var img = (p.featured_image && p.featured_image.url) ? p.featured_image.url : p.image;
      var src = p.price_text !== undefined ? img : sizedImage(img, 120);
      var thumb = img
        ? '<img class="predictive-search__thumb" src="' + escapeHtml(src) +
          '" alt="" width="56" height="56" loading="lazy">'
        : '<span class="predictive-search__thumb predictive-search__thumb--ph" aria-hidden="true"></span>';
      var price = p.price_text !== undefined ? escapeHtml(p.price_text) : formatMoney(p.price, moneyFormat);
      return '<a class="predictive-search__item" role="option" tabindex="-1"' +
        ' id="ps-opt-' + i + '" href="' + escapeHtml(p.url) + '">' +
        thumb +
        '<span class="predictive-search__item-text">' +
          '<span class="predictive-search__item-title">' + escapeHtml(p.title) + '</span>' +
          (price ? '<span class="predictive-search__item-price">' + price + '</span>' : '') +
        '</span>' +
      '</a>';
    }).join('');
    panel.innerHTML =
      '<p class="predictive-search__label">Products</p>' +
      '<div class="predictive-search__list">' + items + '</div>' +
      footerHtml(query);
  }

  function Controller(input) {
    var form = input.closest('form');
    if (!form) return null;

    form.classList.add('predictive-search-host');

    var moneyFormat = input.getAttribute('data-money-format') || '${{amount}}';

    var panel = document.createElement('div');
    panel.className = 'predictive-search';
    panel.setAttribute('role', 'listbox');
    panel.id = 'predictive-search-' + Math.round(Math.random() * 1e6);
    panel.hidden = true;
    form.appendChild(panel);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', panel.id);
    input.setAttribute('autocomplete', 'off');

    var controller = null;
    var activeIndex = -1;

    function options() {
      return Array.prototype.slice.call(panel.querySelectorAll('.predictive-search__item'));
    }

    function open() {
      if (!panel.innerHTML.trim()) return;
      panel.hidden = false;
      form.classList.add('predictive-search-host--open');
      input.setAttribute('aria-expanded', 'true');
    }

    function close() {
      panel.hidden = true;
      form.classList.remove('predictive-search-host--open');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function setActive(i) {
      var opts = options();
      if (!opts.length) return;
      if (i < 0) i = opts.length - 1;
      if (i >= opts.length) i = 0;
      opts.forEach(function (o) { o.classList.remove('is-active'); });
      activeIndex = i;
      var el = opts[i];
      el.classList.add('is-active');
      input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    }

    function query() {
      var q = input.value.trim();
      if (q.length < MIN_CHARS) { close(); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      var params = '?q=' + encodeURIComponent(q) +
        '&resources[type]=product' +
        '&resources[limit]=' + LIMIT +
        '&resources[options][unavailable_products]=last';
      var signal = controller.signal;

      function show(products) {
        render(panel, products, q, moneyFormat);
        activeIndex = -1;
        open();
      }

      function fromJson() {
        return fetch(ENDPOINT + params, { signal: signal, headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (!data) return;
            show((data.resources && data.resources.results && data.resources.results.products) || []);
          });
      }

      fetch(SECTION_ENDPOINT + params + '&section_id=' + SECTION_ID, { signal: signal })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('section ' + r.status)); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('[data-ps-json]');
          if (!node) throw new Error('no data');
          show(JSON.parse(node.textContent));
        })
        .catch(function (e) {
          if (e.name === 'AbortError') return;
          return fromJson();
        })
        .catch(function (e) { if (e.name !== 'AbortError') { close(); } });
    }

    var debouncedQuery = debounce(query, DEBOUNCE);

    input.addEventListener('input', debouncedQuery);

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= MIN_CHARS && panel.innerHTML.trim()) open();
    });

    input.addEventListener('keydown', function (e) {
      var opts = options();
      switch (e.key) {
        case 'ArrowDown':
          if (panel.hidden) { query(); return; }
          e.preventDefault(); setActive(activeIndex + 1); break;
        case 'ArrowUp':
          if (panel.hidden) return;
          e.preventDefault(); setActive(activeIndex - 1); break;
        case 'Enter':
          if (!panel.hidden && activeIndex > -1 && opts[activeIndex]) {
            e.preventDefault();
            window.location.href = opts[activeIndex].getAttribute('href');
          }
          // otherwise let the form submit to native /search
          break;
        case 'Escape':
          if (!panel.hidden) { e.preventDefault(); close(); }
          break;
      }
    });

    // Close on outside click / focus leaving the form.
    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) close();
    });
    form.addEventListener('focusout', function (e) {
      if (!form.contains(e.relatedTarget)) {
        // small delay so click on an option registers before close
        setTimeout(function () {
          if (!form.contains(document.activeElement)) close();
        }, 120);
      }
    });

    return true;
  }

  function init() {
    var inputs = document.querySelectorAll('[data-predictive-search-input]');
    Array.prototype.forEach.call(inputs, function (input) {
      if (input.__predictiveInit) return;
      input.__predictiveInit = true;
      Controller(input);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
